/**
 *
 * @module silkDrmProvider
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/drm.md
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import Token from './../token/entry';
import DrmManager from './drmManager';
import DrmProvider from './drmProvider';

const TokenManager = Token.TokenManager;
const DrmType = Services.Media.DrmType;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class SilkDrmProvider
 * @since 3.2.0
 * @desc SILK is the existing encryption key mechanism.
 * @note A new key service will be introduced as part of the DRM service, but
 * will be fully backwards-compatible with the existing key service.
 * @note In previous versions of the SDK, a EncryptionKeyProvider existed with the same functionality.
 *
 */
export default class SilkDrmProvider extends DrmProvider {

    /**
     *
     * @param {SDK.Drm.DrmManager} provider
     * @param {SDK.Token.TokenManager} tokenManager
     * @param {SDK.Logging.Logger} [logger=Logging]
     *
     */
    constructor(provider, tokenManager, logger = Logging) {

        const errorMessage = {
            ctor: 'SilkDrmProvider(provider, tokenManager, logger)',
            get provider() {
                return `${this.ctor} needs valid provider SDK.Drm.DrmManager instance`;
            },
            get tokenManager() {
                return `${this.ctor} needs SDK.Token.TokenManager instance`;
            }
        };

        instanceStrict(provider, DrmManager, errorMessage.provider, InvalidArgumentException);
        instanceStrict(tokenManager, TokenManager, errorMessage.tokenManager, InvalidArgumentException);

        super(provider, tokenManager, logger);

        /**
         *
         * @access private
         * @type {String}
         * @desc DRM identifier String
         *
         */
        this.type = DrmType.SILK;

        this.logger.log(SilkDrmProvider.name, 'Created.');

    }

    /**
     *
     * @access public
     * @param {String} uri - The encryption key endpoint.
     * @desc Gets the current encryption key via the media service.
     * @emits {MediaFailure} - Raised when a SILK key request fails describing the cause of the problem.
     * @returns {Promise<Array<Byte>>}
     *
     */
    getSilkKey(uri) {

        const errorMessage = {
            method: 'SilkDrmProvider.getSilkKey(uri)',
            get uri() {
                return `${this.method} uri should be a String with length`;
            }
        };

        nonEmptyString(uri, errorMessage.uri, InvalidArgumentException);

        const { provider, logger, failureEvent } = this;

        return provider.getSilkKey(uri).then((silkKey) => {

            return Promise.resolve(silkKey);

        }).catch((exception) => {

            logger.error(SilkDrmProvider.name, 'SILK key request failure.');
            logger.info(SilkDrmProvider.name, `Dispatch ${failureEvent} event.`);

            this.emit(failureEvent, exception);

            return Promise.reject(exception);

        });

    }

}



// WEBPACK FOOTER //
// ./src/drm/silkDrmProvider.js